import React, { useRef, useCallback, useState } from 'react';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { Button, Row, Col } from 'react-bootstrap';
import { useToast } from '../../../hooks/toast';
import api from '../../../services/api';

import Loading from '../../../components/Loading';
import Input from '../../../components/Input';
import getValidationErrors from '../../../utils/getValidationErrors';
import getResponseErrors from '../../../utils/getResponseErrors';
import { optionsUF } from '../../../utils/options';
import SelectSync from '../../../components/SelectSync';
import AsyncSelect from '../../../components/SelectAsync';

export interface IUnidadeSaude {
  UnsCodigo: number;
  UnsNome: string;
  UnsBairro: string;
  UnsCidade: string;
  UnsUF: string;
}

type Props = {
  unidadeSaude?: IUnidadeSaude;
  setShowModal(values: boolean): void;
};

const UnidadeSaudeCadastro: React.FC<Props> = ({
  unidadeSaude,
  setShowModal,
}) => {
  const { addToast } = useToast();
  const unsCodigo = unidadeSaude ? unidadeSaude.UnsCodigo : undefined;

  const formRefUnidadeSaude = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  const [unidadeSaudeUnificar, setUnidadeSaudeUnificar] = useState<
    number | undefined
  >(undefined);

  const addUnidadeSaude = useCallback(
    async (
      dadosUnidadeSaude: IUnidadeSaude,
      codigoUnidade: number | undefined,
    ) => {
      setLoading(true);

      try {
        if (codigoUnidade) {
          await api.put<IUnidadeSaude>(
            `/unidadeSaude/${codigoUnidade}`,
            dadosUnidadeSaude,
          );
        } else {
          await api.post<IUnidadeSaude>('/unidadeSaude', dadosUnidadeSaude);
        }

        addToast({ title: 'Unidade de saúde salva.', type: 'success' });
        setShowModal(false);
      } catch (error) {
        const mensagem = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Não foi possível salvar Unidade de saúde',
          description: mensagem,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast, setShowModal],
  );

  const handleSubmitUnidadeSaude = useCallback(
    async (dados: IUnidadeSaude) => {
      try {
        formRefUnidadeSaude.current?.setErrors({});
        const schema = Yup.object().shape({
          UnsNome: Yup.string()
            .min(3, 'Mínimo 3 caracteres')
            .required('Nome é obrigatório'),
          UnsBairro: Yup.string()
            .min(3, 'Mínimo 3 caracteres')
            .required('Nome é obrigatório'),
          UnsCidade: Yup.string()
            .min(3, 'Mínimo 3 caracteres')
            .required('Nome é obrigatório'),
          UnsUF: Yup.string()
            .min(2, 'Mínimo 2 caracteres')
            .required('Nome é obrigatório'),
        });

        const unidadeSaudeForm = {
          ...dados,
        };

        await schema.validate(unidadeSaudeForm, {
          abortEarly: false,
        });

        await addUnidadeSaude(
          {
            ...unidadeSaudeForm,
          },
          unsCodigo,
        );
      } catch (error: any) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefUnidadeSaude.current?.setErrors(errors);
          return;
        }

        addToast({
          type: 'error',
          title: 'Erro ao salvar unidade de saúde',
          description: error?.response?.data?.message
            ? error?.response?.data?.message
            : error.message,
        });
      } finally {
        //  setLoading(false);
      }
    },
    [addToast, addUnidadeSaude, unsCodigo],
  );

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleOnChangeVazio = useCallback(() => {}, []);

  const handleSelectChange = useCallback(({ value }) => {
    if (value && value !== '') {
      setUnidadeSaudeUnificar(Number(value));
    }
  }, []);

  const handleUnificar = useCallback(async () => {
    if (unsCodigo === unidadeSaudeUnificar)
      addToast({
        title: 'Unificar Unidade de Saúde',
        type: 'error',
        description: 'Mesma Unidade de Saúde selecionada.',
      });

    setLoading(true);

    try {
      const dadosUnificar = { UnsCodigoUnificar: unidadeSaudeUnificar };
      await api.put(`/unidadeSaude/unificar/${unsCodigo}`, dadosUnificar);

      addToast({
        title: 'Unidade de Saúde unificada com sucesso',
        type: 'success',
      });
    } catch (error) {
      const mensagem = getResponseErrors(error);
      addToast({
        type: 'error',
        title: 'Não foi possível unificar Unidade de Saúde',
        description: mensagem,
      });
    } finally {
      setLoading(false);
    }
  }, [unsCodigo, unidadeSaudeUnificar, addToast]);

  return (
    <>
      {loading && <Loading />}
      <div className="container">
        <Row noGutters>
          <Col md={12}>
            <Form
              ref={formRefUnidadeSaude}
              initialData={unidadeSaude}
              onSubmit={handleSubmitUnidadeSaude}
            >
              <Row>
                <Col md={12} className="form-group pr-2">
                  <Input name="UnsNome" label="Nome" maxLength={100} />
                </Col>
              </Row>
              <Row>
                <Col md={4} className="pr-md-2">
                  <Input name="UnsBairro" label="Bairro" minLength={4} />
                </Col>
                <Col md={4} className="form-group pr-2">
                  <Input
                    name="UnsCidade"
                    label="Cidade"
                    type="text"
                    minLength={4}
                  />
                </Col>

                <Col md={4} className="form-group pr-2">
                  <SelectSync
                    name="UnsUF"
                    label="UF"
                    placeholder="Selecione"
                    handleSelectChange={handleOnChangeVazio}
                    options={optionsUF}
                    isDisabled={unsCodigo !== undefined}
                  />
                </Col>
              </Row>
              {unsCodigo !== undefined && (
                <Row className={`${unsCodigo === undefined ? 'd-none' : ''}`}>
                  <Col md={6} className="form-group pr-2">
                    <AsyncSelect
                      urlApi="unidadesaude/combo?termoBusca="
                      name="AgeUnidadeSaude"
                      isDisabled={unsCodigo === undefined}
                      label="Unidade de sáude a Unificar"
                      placeholder="Digite para buscar"
                      handleSelectChange={handleSelectChange}
                      isClearable
                    />
                  </Col>
                  <Col md="6">
                    <div className="light-grey text-left pt-3">
                      <>
                        <Button
                          type="button"
                          variant="primary"
                          onClick={() => handleUnificar()}
                          disabled={loading}
                        >
                          Unificar
                        </Button>
                      </>
                    </div>
                  </Col>
                </Row>
              )}
              <Row className="align-items-center border-top p-1">
                <Col md="12">
                  <div className="light-grey text-left pt-3">
                    <>
                      <Button
                        type="button"
                        variant="success"
                        className="mt-2"
                        onClick={
                          () => formRefUnidadeSaude.current?.submitForm()
                          // eslint-disable-next-line react/jsx-curly-newline
                        }
                      >
                        Salvar dados
                      </Button>
                    </>
                  </div>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default UnidadeSaudeCadastro;
